import React from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import { connect } from "react-redux";
import { User as OidcUser } from "oidc-client";
import { Container, Row, Col, Card, CardBody, CardTitle } from "reactstrap";
import FeedbackListContainer from "../../../Feedback/Components/Common/FeedbackList/FeedbackListContainer";
import { FeedbackCategory, FeedbackSortOption } from "../../../Feedback/Constants/feedbackEnums";
import {
  appPaths,
  LoadingState,
  ResponseError,
  LoadingStateHelper,
  ResponseErrorHelper,
  ApiRequest,
  ErrorDisplay,
  LoadingDisplay,
  IAppState
} from "../../../Common";
import UserProfileGeneralInfo from "./UserProfileGeneralInfo";
import { ResponseResult } from "../../../Common/Models/ResponseResult";
import ResponseHandler from "../../../App/ResponseHandler";
import { StatusCode } from "../../../App/Enums/StatusCode";
import { User } from "../..";
import { endpoints } from "../../Constants/endpoints";

type TParams = {
  username: string;
};

type State = {
  user?: User;
  loadingState: LoadingState;
  loadingError?: ResponseError;
};

type StateProps = {
  loggedInUser?: OidcUser;
};

const mapStateToProps = (state: IAppState): StateProps => ({
  loggedInUser: state.oidc.user
});

const FEATURE_REQUEST = [FeedbackCategory.FeatureRequest];
const BUG_REPORT = [FeedbackCategory.BugReport];
const RESOURCE_REQUEST = [FeedbackCategory.ResourceRequest];

class UserProfileContainer extends React.Component<
  RouteComponentProps<TParams> & StateProps,
  State
> {
  constructor(props: RouteComponentProps<TParams>) {
    super(props);

    this.state = {
      loadingState: {
        errored: false,
        isLoaded: false,
        isLoading: false
      }
    };
  }

  componentDidMount = () => {
    if (!this.state.loadingState.isLoaded && !this.state.loadingState.isLoading) {
      this.loadUser();
    }
  };

  loadUser = () => {
    this.setState({
      loadingState: LoadingStateHelper.generate(false, true, false)
    });

    ApiRequest.fromPath(endpoints.users.getByUsername(this.props.match.params.username))
      .withAuthHeader()
      .get()
      .then(response => {
        ResponseHandler.handle(
          response,
          (result: ResponseResult<User>) => {
            this.setState(
              {
                user: result.payload
              },
              () => this.setState({ loadingState: LoadingStateHelper.generate(false, false, true) })
            );
          },
          result => {
            this.setState({
              loadingState: LoadingStateHelper.generate(true, false, false),
              loadingError: ResponseErrorHelper.generate(
                "Failed to load user",
                result.error.message,
                response.status
              )
            });
          },
          () => {
            this.setState({
              loadingState: LoadingStateHelper.generate(true, false, false),
              loadingError: ResponseErrorHelper.generate(
                "Failed to load user",
                "An unknown error has occured",
                response.status
              )
            });
          }
        ).catch(() => {
          this.setState({
            loadingState: LoadingStateHelper.generate(true, false, false),
            loadingError: ResponseErrorHelper.generate(
              "Failed to load user",
              "Unable to parse response JSON",
              response.status
            )
          });
        });
      })
      .catch(() => {
        this.setState({
          loadingState: LoadingStateHelper.generate(true, false, false),
          loadingError: ResponseErrorHelper.generate(
            "Failed to load user",
            "Failed to contact server",
            StatusCode.Status503ServiceUnavailable
          )
        });
      });
  };

  render() {
    if (this.state.loadingState.errored) {
      return <ErrorDisplay {...this.state.loadingError} />;
    }

    if (this.state.loadingState.isLoading && !this.state.loadingState.isLoaded) {
      return <LoadingDisplay />;
    }

    if (!this.state.user) {
      return <LoadingDisplay />;
    }

    const isCMUser =
      this.props.loggedInUser &&
      this.props.loggedInUser.profile.companyName &&
      this.props.loggedInUser.profile.companyName.toLowerCase() === "cadmakers";

    return (
      <Container fluid>
        <Row>
          <Col lg={10}>
            <h2>{(this.state.user as User).name} Public Profile</h2>
          </Col>
        </Row>
        <br />
        <Row>
          <Card style={{ width: "100%" }}>
            <CardBody>
              <CardTitle>
                <h4>General Information</h4>
              </CardTitle>
              <UserProfileGeneralInfo username={this.props.match.params.username} />
              {isCMUser && (
                <Link
                  to={appPaths.user.assigned.replace(":username", this.props.match.params.username)}
                >
                  View Assigned Feedbacks
                </Link>
              )}
            </CardBody>
          </Card>
        </Row>
        <br />
        <Row>
          <Card style={{ width: "100%" }}>
            <CardBody>
              <CardTitle>
                <h4>
                  Recent Feature Requests{" "}
                  <Link
                    to={appPaths.user.featureRequests.replace(
                      ":username",
                      this.props.match.params.username
                    )}
                  >
                    (See All)
                  </Link>
                </h4>
              </CardTitle>
              <FeedbackListContainer
                listId={`APP_USER_PROFILE_FEATURE_REQUEST_LIST${this.props.match.params.username}`}
                maxNumToGet={5}
                seeMoreLink={{
                  url: appPaths.user.featureRequests.replace(
                    ":username",
                    this.props.match.params.username
                  ),
                  text: "See All..."
                }}
                sort={FeedbackSortOption.DateCreated}
                isAscending={false}
                reorderable={false}
                enableSearch={false}
                username={this.props.match.params.username}
                virtualizeDisabled
                selectedCategories={FEATURE_REQUEST}
              />
            </CardBody>
          </Card>
        </Row>
        <br />
        <Row>
          <Card style={{ width: "100%" }}>
            <CardBody>
              <CardTitle>
                <h4>
                  Recent Bug Reports{" "}
                  <Link
                    to={appPaths.user.bugReports.replace(
                      ":username",
                      this.props.match.params.username
                    )}
                  >
                    (See All)
                  </Link>
                </h4>
              </CardTitle>
              <FeedbackListContainer
                listId={`APP_USER_PROFILE_BUG_REPORT_LIST${this.props.match.params.username}`}
                maxNumToGet={5}
                seeMoreLink={{
                  url: appPaths.user.bugReports.replace(
                    ":username",
                    this.props.match.params.username
                  ),
                  text: "See All..."
                }}
                sort={FeedbackSortOption.DateCreated}
                isAscending={false}
                reorderable={false}
                enableSearch={false}
                username={this.props.match.params.username}
                virtualizeDisabled
                selectedCategories={BUG_REPORT}
              />
            </CardBody>
          </Card>
        </Row>
        <br />
        <Row>
          <Card style={{ width: "100%" }}>
            <CardBody>
              <CardTitle>
                <h4>
                  Recent Resource Requests{" "}
                  <Link
                    to={appPaths.user.resourceRequests.replace(
                      ":username",
                      this.props.match.params.username
                    )}
                  >
                    (See All)
                  </Link>
                </h4>
              </CardTitle>
              <FeedbackListContainer
                listId={`APP_USER_PROFILE_RESOURCE_REQUEST_LIST${this.props.match.params.username}`}
                maxNumToGet={5}
                seeMoreLink={{
                  url: appPaths.user.resourceRequests.replace(
                    ":username",
                    this.props.match.params.username
                  ),
                  text: "See All..."
                }}
                sort={FeedbackSortOption.DateCreated}
                isAscending={false}
                reorderable={false}
                enableSearch={false}
                username={this.props.match.params.username}
                virtualizeDisabled
                selectedCategories={RESOURCE_REQUEST}
              />
            </CardBody>
          </Card>
        </Row>
        <br />
      </Container>
    );
  }
}

export default connect(mapStateToProps)(UserProfileContainer);
